import { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  MessageSquare, 
  FileText, 
  Image as ImageIcon,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  ArrowUpRight,
  Calendar
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar,
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import './DashboardModern.css';

const COLORS = {
  primary: '#ff6b35',
  secondary: '#f7931e',
  gold: '#ffd700',
  success: '#22c55e',
  warning: '#fbbf24',
  danger: '#ef4444',
};

const DashboardModern = ({ stats, testimonials = [], getAuthHeader }) => {
  const [recentActivities, setRecentActivities] = useState([]);
  const [testimonialsData, setTestimonialsData] = useState([]);

  useEffect(() => {
    // Generate mock data for testimonials trend (last 7 days)
    const generateTestimonialsTrend = () => {
      const data = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(new Date(), i);
        data.push({
          date: format(date, 'dd MMM', { locale: fr }),
          approved: Math.floor(Math.random() * 5) + 1,
          pending: Math.floor(Math.random() * 3),
        });
      }
      return data;
    };

    setTestimonialsData(generateTestimonialsTrend());

    // Generate recent activities from testimonials
    if (testimonials && testimonials.length > 0) {
      const activities = testimonials
        .slice(0, 5)
        .map(t => ({
          id: t.id,
          type: t.approved ? 'approved' : 'pending',
          title: t.approved ? 'Témoignage approuvé' : 'Nouveau témoignage',
          description: `${t.name} - ${t.event_type}`,
          time: t.date || 'Récent',
          icon: t.approved ? 'check' : 'clock',
        }));
      setRecentActivities(activities);
    }
  }, [testimonials]);

  if (!stats) {
    return <div className="dashboard-loading">Chargement...</div>;
  }

  const statsCards = [
    {
      title: 'Témoignages Totaux',
      value: stats.total_testimonials || 0,
      icon: <MessageSquare size={24} />,
      color: 'primary',
      trend: '+12%',
      trendUp: true,
    },
    {
      title: 'En Attente',
      value: stats.pending_testimonials || 0,
      icon: <Clock size={24} />,
      color: 'warning',
      trend: stats.pending_testimonials > 5 ? 'Action requise' : 'Normal',
      trendUp: false,
    },
    {
      title: 'Approuvés',
      value: stats.approved_testimonials || 0,
      icon: <CheckCircle size={24} />,
      color: 'success',
      trend: '+8%',
      trendUp: true,
    },
    {
      title: 'Demandes de Devis',
      value: stats.total_quotes || 0,
      icon: <FileText size={24} />,
      color: 'primary',
      trend: '+15%',
      trendUp: true,
    },
    {
      title: 'Items Galerie',
      value: stats.total_gallery_items || 0,
      icon: <ImageIcon size={24} />,
      color: 'secondary',
      trend: 'Stable',
      trendUp: null,
    },
    {
      title: 'Messages Contact',
      value: stats.total_contacts || 0,
      icon: <Mail size={24} />,
      color: 'info',
      trend: '+5%',
      trendUp: true,
    },
  ];

  // Data for pie chart
  const pieData = [
    { name: 'Approuvés', value: stats.approved_testimonials || 0 },
    { name: 'En attente', value: stats.pending_testimonials || 0 },
  ];

  const PIE_COLORS = [COLORS.success, COLORS.warning];

  return (
    <div className="dashboard-modern">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Tableau de Bord</h1>
          <p className="dashboard-subtitle">
            Bienvenue ! Voici un aperçu de votre activité.
          </p>
        </div>
        <div className="dashboard-header-date">
          <Calendar size={20} />
          <span>{format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}</span>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="stats-grid">
        {statsCards.map((stat, index) => (
          <Card key={index} className="stat-card" hover>
            <div className="stat-card-content">
              <div className="stat-card-header">
                <div className={`stat-icon stat-icon--${stat.color}`}>
                  {stat.icon}
                </div>
                <div className="stat-card-info">
                  <p className="stat-label">{stat.title}</p>
                  <h3 className="stat-value">{stat.value}</h3>
                </div>
              </div>
              {stat.trend && (
                <div className="stat-trend">
                  {stat.trendUp !== null && (
                    <ArrowUpRight 
                      size={16} 
                      className={stat.trendUp ? 'trend-up' : 'trend-down'}
                      style={{ transform: stat.trendUp ? 'none' : 'rotate(90deg)' }}
                    />
                  )}
                  <span className={stat.trendUp ? 'trend-up' : stat.trendUp === false ? 'trend-down' : ''}>
                    {stat.trend}
                  </span>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-grid">
        {/* Testimonials Trend Chart */}
        <Card 
          title="Évolution des Témoignages" 
          subtitle="7 derniers jours"
          className="chart-card"
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={testimonialsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis 
                dataKey="date" 
                stroke="#b0b0b0"
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis 
                stroke="#b0b0b0"
                style={{ fontSize: '0.875rem' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #ff6b35',
                  borderRadius: '8px',
                  color: '#ffffff',
                }}
              />
              <Legend 
                wrapperStyle={{ color: '#b0b0b0' }}
              />
              <Line 
                type="monotone" 
                dataKey="approved" 
                stroke={COLORS.success} 
                strokeWidth={3}
                dot={{ fill: COLORS.success, r: 4 }}
                activeDot={{ r: 6 }}
                name="Approuvés"
              />
              <Line 
                type="monotone" 
                dataKey="pending" 
                stroke={COLORS.warning} 
                strokeWidth={3}
                dot={{ fill: COLORS.warning, r: 4 }}
                activeDot={{ r: 6 }}
                name="En attente"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* Testimonials Distribution Pie Chart */}
        <Card 
          title="Répartition des Témoignages"
          subtitle="État actuel"
          className="chart-card"
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #ff6b35',
                  borderRadius: '8px',
                  color: '#ffffff',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Recent Activities & Quick Actions */}
      <div className="bottom-grid">
        {/* Recent Activities */}
        <Card 
          title="Activités Récentes" 
          subtitle="Dernières actions"
          className="activities-card"
        >
          <div className="activities-list">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className={`activity-icon activity-icon--${activity.type}`}>
                    {activity.icon === 'check' ? (
                      <CheckCircle size={20} />
                    ) : activity.icon === 'clock' ? (
                      <Clock size={20} />
                    ) : (
                      <XCircle size={20} />
                    )}
                  </div>
                  <div className="activity-content">
                    <p className="activity-title">{activity.title}</p>
                    <p className="activity-description">{activity.description}</p>
                  </div>
                  <div className="activity-time">{activity.time}</div>
                </div>
              ))
            ) : (
              <p className="activities-empty">Aucune activité récente</p>
            )}
          </div>
        </Card>

        {/* Quick Actions */}
        <Card 
          title="Actions Rapides" 
          subtitle="Accès direct"
          className="quick-actions-card"
        >
          <div className="quick-actions-grid">
            <div className="quick-action-item">
              <div className="quick-action-icon quick-action-icon--primary">
                <MessageSquare size={24} />
              </div>
              <div className="quick-action-content">
                <h4>Gérer les Témoignages</h4>
                <p>{stats.pending_testimonials || 0} en attente</p>
              </div>
            </div>
            <div className="quick-action-item">
              <div className="quick-action-icon quick-action-icon--secondary">
                <FileText size={24} />
              </div>
              <div className="quick-action-content">
                <h4>Demandes de Devis</h4>
                <p>{stats.total_quotes || 0} total</p>
              </div>
            </div>
            <div className="quick-action-item">
              <div className="quick-action-icon quick-action-icon--success">
                <ImageIcon size={24} />
              </div>
              <div className="quick-action-content">
                <h4>Galerie</h4>
                <p>{stats.total_gallery_items || 0} items</p>
              </div>
            </div>
            <div className="quick-action-item">
              <div className="quick-action-icon quick-action-icon--info">
                <Mail size={24} />
              </div>
              <div className="quick-action-content">
                <h4>Messages</h4>
                <p>{stats.total_contacts || 0} contacts</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardModern;
